import Logo from "images/rs-logo.svg";
import { useEffect, useState } from "react";
import * as FP from "utils/fp-js";
import ProfileMobileButton from "images/profile-mobile-button.svg";
import { Profile } from "./Profile";
import { mobileModes } from "../Dashboard";
import { GoBack } from "UI/GoBack";
import { MobileRewards } from "./MobileRewards";
import clsx from "clsx";
// import { ReactComponent as CallUsIcon } from "images/call-us-icon.svg";
import { ReactComponent as SignOutIcon } from "images/logout-icon.svg";
import { ReactComponent as SettingsIcon } from "images/settings-icon.svg";
import { Confirmation } from "./Confirmation";
import { MessageIndicator } from "./MessageIndicator";
import { useMessageIndicator } from "hooks/useMessageIndicator";
import { IconButton } from "@mui/material";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { ReactComponent as LogoutIcon } from "images/logout-icon.svg";
import { ReactComponent as PasswordIcon } from "images/password-icon.svg";
import { ReactComponent as AppleLogo } from "images/apple-logo.svg";
import { WarningWindow } from "./WarningWindow";
import { ReactComponent as QRCode } from "images/qr-code.svg";

export const MenuItem = ({ label, icon = null, onClick = () => null }) => {
  return (
    <div
      className={clsx(
        "bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-500",
        "rounded-xl flex flex-row items-center justify-between p-2 pl-4 pr-4 gap-2",
        "cursor-pointer"
      )}
      onClick={onClick}
    >
      {!!icon && <div className="text-xl">{icon}</div>}
      <div className="font-bold">{label}</div>
    </div>
  );
};

export const Layout = ({
  content,
  progress,
  userLevel,
  originator,
  levelProgress,
  dialogs,
  isRecipientAdmin,
  hanldeLogout,
  showError,
  setSelectedReferralId,
}) => {
  const [mode, setMode] = useState(mobileModes.Dashboard);
  const [confirmation, setConfirmation] = useState(null);
  const [newMessagesNumber, setNewMessagesNumber] = useState(0);
  const [isUserMenuOpened, setIsUserMenuOpened] = useState(false);
  const [isQrCodeDialogOpened, setIsQrCodeDialogOpened] = useState(false);
  const [isPasswordChangeDialogOpened, setIsPasswordChangeDialogOpened] =
    useState(false);

  useMessageIndicator({
    newMessagesNumber,
    setNewMessagesNumber,
    userData: originator,
    showError,
  });

  const logout = () => {
    setIsUserMenuOpened(false);
    setConfirmation({
      title: "Please confirm",
      message: "Are you sure you want to sign out?",
      proceedFn: hanldeLogout,
      cancelFn: () => setConfirmation(null),
    });
  };

  useEffect(() => {
    if (isUserMenuOpened) {
      const closeMenu = () => setIsUserMenuOpened(false);
      setTimeout(() => document.addEventListener("click", closeMenu));
      return () => document.removeEventListener("click", closeMenu);
    }
  }, [isUserMenuOpened]);

  return (
    <>
      <div className="bg-white hidden md:flex flex-row justify-between p-4 pl-8 pr-8 items-center w-full">
        <div className="font-bold text-xl">Digital Buttons Dashboard</div>
        <div className="flex flex-row items-center gap-4">
          {/* <MenuItem label="Contact Us" icon={<CallUsIcon />} /> */}
          <MessageIndicator
            newMessages={newMessagesNumber}
            showError={showError}
            userData={originator}
            setSelectedReferralId={setSelectedReferralId}
          />
          <div className="flex flex-col">
            <IconButton onClick={() => setIsUserMenuOpened(!isUserMenuOpened)}>
              <SettingsIcon className="text-gray-400" />
            </IconButton>
            {isUserMenuOpened && (
              <div className="relative">
                <div
                  className={clsx(
                    "absolute top-2 right-0 -mt-1 -mr-1 bg-white text-black text-sm",
                    "w-48 flex items-center justify-center shadow-lg rounded-md",
                    "border border-gray-300 z-10",
                    "flex flex-col justify-start items-stretch divide-y divide-gray-200"
                  )}
                >
                  <div
                    className="flex flex-row justify-start items-center cursor-pointer p-3 select-none hover:bg-blue-50"
                    onClick={logout}
                  >
                    <LogoutIcon className="w-5 h-5 mr-2" />
                    Sign out
                  </div>
                  <div
                    className="flex flex-row justify-start items-center cursor-pointer p-3 select-none hover:bg-blue-50"
                    onClick={() => setIsPasswordChangeDialogOpened(true)}
                  >
                    <PasswordIcon className="w-5 h-5 mr-2" />
                    Change password
                  </div>
                </div>
              </div>
            )}
          </div>
          <div
            className={clsx(
              "bg-gray-100 p-2 flex flex-row justify-start items-center rounded-xl cursor-pointer gap-1"
            )}
            onClick={() => setIsQrCodeDialogOpened((prev) => !prev)}
          >
            <AppleLogo />
            <div className="text-sm font-bold text-black">Download iOS App</div>
          </div>
          <img src={Logo} className="w-20" alt="referral stream logo" />
        </div>
      </div>
      <div className="flex-1 flex flex-col xl:flex-row 2xl:flex-row xl:justify-between 2xl:justify-between bg-gray-100 px-2 py-4 h-full">
        {mode === mobileModes.Dashboard && (
          <div className="bg-white rounded-xl m-4 shadow p-4 flex flex-row justify-between md:hidden xl:hidden 2xl:hidden">
            <img
              src={ProfileMobileButton}
              onClick={() => setMode(mobileModes.Profile)}
              className="w-10 cursor-pointer hover:opacity-80"
              alt="profile button"
            />
            <div className="flex flex-row gap-2">
              <MessageIndicator
                newMessages={newMessagesNumber}
                showError={showError}
                userData={originator}
                setSelectedReferralId={setSelectedReferralId}
              />
              <MenuItem
                label="Sign out"
                icon={<SignOutIcon />}
                onClick={logout}
              />
              <img src={Logo} className="w-16" alt="referral stream logo" />
            </div>
          </div>
        )}
        <div className="md:hidden lg:hidden xl:hidden 2xl:hidden flex-1 flex flex-row items-stretch">
          {FP.match(mode)
            .on(mobileModes.Profile, () => (
              <div className="bg-white flex-1 rounded-xl m-4 shadow p-4 flex sx:hidden flex-col sm:flex-col md:flex-col lg:flex-col xl:flex-row 2xl:flex-row justify-stretch items-stretch">
                <Profile
                  progress={progress}
                  userLevel={userLevel}
                  originator={originator}
                  levelProgress={levelProgress}
                  asPage
                  setMode={setMode}
                  isRecipientAdmin={isRecipientAdmin}
                />
              </div>
            ))
            .on(mobileModes.Awards, () => (
              <div className="bg-white flex-1 rounded-xl m-4 shadow px-8 py-4 flex sx:hidden flex-col sm:flex-col md:flex-col lg:flex-col xl:flex-row 2xl:flex-row justify-stretch items-stretch">
                <GoBack
                  hanldeClick={() => setMode(mobileModes.Profile)}
                  label="Rewards"
                />
                <MobileRewards progress={progress} userLevel={userLevel} />
              </div>
            ))
            .otherwise(
              <div className="content flex-grow bg-white rounded-xl m-4 shadow p-4 pt-1 overflow-x-hidden">
                {content}
              </div>
            )}
        </div>
        <div className="bg-white rounded-xl m-4 shadow p-4 hidden sm:hidden md:flex lg:flex xl:flex 2xl:flex flex-col sm:flex-col md:flex-col lg:flex-col xl:flex-row 2xl:flex-row justify-stretch items-stretch">
          <Profile
            progress={progress}
            userLevel={userLevel}
            originator={originator}
            levelProgress={levelProgress}
            isRecipientAdmin={isRecipientAdmin}
          />
        </div>
        <div className="content flex-col hidden sm:hidden md:flex lg:flex xl:flex 2xl:flex flex-grow bg-white rounded-xl m-4 shadow p-8 pt-1">
          {content}
        </div>
        {dialogs}
        {confirmation && <Confirmation {...confirmation} />}
      </div>
      {isPasswordChangeDialogOpened && (
        <ChangePasswordDialog
          handleClose={() => setIsPasswordChangeDialogOpened(false)}
          dashboardAccessToken={originator?.dashboardAccessToken}
        />
      )}
      {isQrCodeDialogOpened && (
        <WarningWindow
          title={null}
          content={
            <div className="flex flex-col justify-center items-center gap-3">
              <QRCode className="my-2 w-36 h-36" />
              <div className="text-xl font-bold text-black">
                Referral Stream iOS App
              </div>
              <div className="text-sm text-gray-600 max-w-96 text-center">
                Scan the QR code to download the Referral Stream iOS app and
                manage referrals on the go.
              </div>
            </div>
          }
          handleClose={() => setIsQrCodeDialogOpened(false)}
          withCloseButton
          withoutActions
        />
      )}
    </>
  );
};
